import { defineStore } from 'pinia'
import { apiClient as api } from '@/services/api'

export interface SafeboxUser {
    id: string
    name: string
    avatar: string | null
    isVip?: boolean
    eventTeam?: { id: number; name: string } | null
}

export interface SafeboxRound {
    id: number
    jackpot: number
    attemptPrice: number
    winningCode?: number | null
    winner?: SafeboxUser | null
    createdAt?: string
    updatedAt?: string
}

export interface SafeboxAttempt {
    id: string
    user: SafeboxUser
    createdAt: string
    bonusWon: boolean
    bonusType?: boolean | null
    bonusValue?: boolean | null
}

export interface SafeboxState {
    currentRound: SafeboxRound | null
    recentAttempts: SafeboxAttempt[]
    recentRounds: SafeboxRound[]
    isLoading: boolean
    isSubmitting: boolean
    error: string | null
}

import { useProfileStore } from '@/store/profile'

export const useSafeboxStore = defineStore('safebox', {
    state: (): SafeboxState => ({
        currentRound: null,
        recentAttempts: [],
        recentRounds: [],
        isLoading: false,
        isSubmitting: false,
        error: null,
    }),

    actions: {
        async fetchRecent() {
            this.isLoading = true
            this.error = null
            try {
                const response = await api.get('/v1/safebox/recent')
                // Handle response based on docs/safebox-ui-guide.md
                // Response: { round, attempts, recentRounds }
                const data = response.data

                this.currentRound = data.round || null
                this.recentAttempts = data.attempts || []
                this.recentRounds = data.recentRounds || []
            } catch (err: any) {
                console.error('Failed to fetch safebox data:', err)
                this.error = err.message || 'Failed to load game data'
            } finally {
                this.isLoading = false
            }
        },

        async submitAttempt(code: string): Promise<{ correct: boolean; bonus: boolean | null }> {
            if (!this.currentRound) {
                throw new Error('No active round')
            }

            this.isSubmitting = true
            this.error = null

            try {
                // Parse code to number if API expects number, guide says "code": number
                const numericCode = parseInt(code, 10)

                const response = await api.post(`/v1/safebox/${this.currentRound.id}/attempt`, {
                    code: numericCode
                })



                // success - refresh balance
                const profileStore = useProfileStore()
                profileStore.updateWallet()

                return response.data
            } catch (err: any) {
                console.error('Failed to submit attempt:', err)
                if (err.response?.data?.message) {
                    this.error = err.response.data.message
                } else {
                    this.error = 'Failed to submit code'
                }
                throw err
            } finally {
                this.isSubmitting = false
            }
        },

        // SSE Handlers
        addAttempt(attempt: SafeboxAttempt) {
            this.recentAttempts.unshift(attempt)
            if (this.recentAttempts.length > 20) {
                this.recentAttempts.pop()
            }
        },

        updateJackpot(amount: number) {
            if (this.currentRound) {
                this.currentRound.jackpot = amount
            }
        },

        // When a round ends or a new one starts (from SSE or polling)
        setRound(round: SafeboxRound) {
            this.currentRound = round
        }
    }
})
